import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SettingsContext = createContext(null);

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within SettingsProvider');
  }
  return context;
};

export const SettingsProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [settings, setSettings] = useState({
    upcoming_days: 14,
    auto_fill_count: 7,
    show_active_in_storage: true,
    contexts_enabled: false,
    assignee_tags_enabled: false,
    drag_handle_position: 'right',
    horizontal_groups: true,
    energy_tags_enabled: true,
    storage_active_visibility: 'visible'
  });
  const [contexts, setContexts] = useState([]);
  const [selectedContextId, setSelectedContextId] = useState(null);
  const [activeLists, setActiveLists] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchSettings = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/settings`, {
        withCredentials: true
      });
      setSettings(response.data);
      
      // If contexts are enabled, fetch them
      if (response.data.contexts_enabled) {
        const contextsResponse = await axios.get(`${API}/contexts`, {
          withCredentials: true
        });
        setContexts(contextsResponse.data);
        // Select first context by default if none selected
        if (contextsResponse.data.length > 0 && !selectedContextId) {
          setSelectedContextId(contextsResponse.data[0].id);
        }
      }

      // Always fetch active lists (endpoint ensures a default list exists)
      const listsResponse = await axios.get(`${API}/active_lists`, {
        withCredentials: true
      });
      setActiveLists(listsResponse.data || []);
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, selectedContextId]);

  // Fetch settings when authenticated
  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const updateSettings = async (newSettings) => {
    try {
      const response = await axios.patch(`${API}/settings`, newSettings, {
        withCredentials: true
      });
      setSettings(response.data);
      
      // If contexts were just enabled, fetch them
      if (newSettings.contexts_enabled === true) {
        const contextsResponse = await axios.get(`${API}/contexts`, {
          withCredentials: true
        });
        setContexts(contextsResponse.data);
        if (contextsResponse.data.length > 0) {
          setSelectedContextId(contextsResponse.data[0].id);
        }
      }
      
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error updating settings:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Failed to update settings' 
      };
    }
  };

  const fetchContexts = async () => {
    try {
      const response = await axios.get(`${API}/contexts`, {
        withCredentials: true
      });
      setContexts(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching contexts:', error);
      return [];
    }
  };

  const fetchActiveLists = async () => {
    try {
      const response = await axios.get(`${API}/active_lists`, {
        withCredentials: true
      });
      setActiveLists(response.data || []);
      return response.data || [];
    } catch (error) {
      console.error('Error fetching active lists:', error);
      return [];
    }
  };

  const value = {
    settings,
    loading,
    fetchSettings,
    updateSettings,
    // Contexts
    contexts,
    setContexts,
    fetchContexts,
    selectedContextId,
    setSelectedContextId,
    // Active Lists
    activeLists,
    setActiveLists,
    fetchActiveLists,
    // Convenience accessors
    contextsEnabled: settings.contexts_enabled,
    assigneeTagsEnabled: settings.assignee_tags_enabled,
    energyTagsEnabled: settings.energy_tags_enabled,
    upcomingDays: settings.upcoming_days,
    autoFillCount: settings.auto_fill_count,
    dragHandlePosition: settings.drag_handle_position,
    horizontalGroups: settings.horizontal_groups,
    storageActiveVisibility: settings.storage_active_visibility || 'visible',
    taskCardTagsDisplay: settings.task_card_tags_display || 'icon_text'
  };

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
};
